import { useCallback, useEffect, useRef } from "react";
import { TURNSTILE_SITE_KEY } from "data/constants";

/**
 * Cloudflare Turnstile integration used to protect the endpoints that send
 * emails (POST /api/email/support) from automated spam.
 *
 * The widget is rendered in "execute" mode with `interaction-only` appearance:
 * nothing is shown to the user unless Cloudflare decides a challenge is needed,
 * so the forms keep their current layout. `getToken()` runs the challenge on
 * demand (on submit) and resolves with a single-use token that must be sent to
 * the backend as `captcha_token`.
 *
 * When REACT_APP_TURNSTILE_SITE_KEY is not set the hook is inert and
 * `getToken()` resolves to `null` (local development). The backend applies the
 * same rule on its own secret, so both sides must be configured together.
 */

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  execution?: "render" | "execute";
  appearance?: "always" | "execute" | "interaction-only";
  theme?: "auto" | "light" | "dark";
  callback?: (token: string) => void;
  "error-callback"?: (code?: string) => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
};

type TurnstileApi = {
  render: (el: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  execute: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
/**
 * Turnstile defaults to `auto`, which follows the visitor's OS color scheme and
 * renders a dark widget on our light pages. The app is light-only (the
 * SwitchDarkMode toggle is not mounted anywhere), so pin the widget to light.
 */
const WIDGET_THEME = "light" as const;
/** Interactive challenges are slow; give the user a generous window. */
const TOKEN_TIMEOUT_MS = 120_000;

let scriptPromise: Promise<void> | null = null;

/** Injects the Turnstile API script once per page and resolves when ready. */
const loadTurnstileScript = (): Promise<void> => {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => {
      // Allow a later attempt to retry the download.
      scriptPromise = null;
      reject(new Error("Unable to load the Turnstile script"));
    });
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
};

/**
 * Fallback host for flows that have no form to anchor the widget to (e.g. the
 * cart "send request" buttons): a floating container pinned to the viewport,
 * only ever visible while an interactive challenge is running.
 */
const createFloatingContainer = (): HTMLDivElement => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.right = "1rem";
  container.style.bottom = "1rem";
  container.style.zIndex = "9999";
  document.body.appendChild(container);
  return container;
};

export interface UseTurnstileResult {
  /**
   * Attach to an empty <div> inside the form: the widget renders there. When
   * left unattached the widget falls back to a floating container.
   */
  captchaRef: React.RefObject<HTMLDivElement>;
  /** True when a site key is configured, i.e. the CAPTCHA is enforced. */
  isEnabled: boolean;
  /**
   * Runs the challenge and resolves with a fresh single-use token, or `null`
   * when the CAPTCHA is disabled. Rejects when the challenge fails or times out.
   */
  getToken: () => Promise<string | null>;
}

export const useTurnstile = (action?: string): UseTurnstileResult => {
  const captchaRef = useRef<HTMLDivElement>(null);
  const floatingContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const pendingRef = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const isEnabled = Boolean(TURNSTILE_SITE_KEY);

  const settle = useCallback(
    (token: string | null, error?: Error) => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      if (token) pending.resolve(token);
      else pending.reject(error ?? new Error("Turnstile verification failed"));
    },
    []
  );

  // Clean up the widget (and any request still waiting) on unmount.
  useEffect(() => {
    return () => {
      settle(null, new Error("Turnstile widget unmounted"));
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      floatingContainerRef.current?.remove();
      floatingContainerRef.current = null;
    };
  }, [settle]);

  const ensureWidget = useCallback(async (): Promise<string> => {
    if (widgetIdRef.current) return widgetIdRef.current;
    await loadTurnstileScript();
    if (!window.turnstile) {
      throw new Error("Turnstile is not available");
    }
    if (!captchaRef.current && !floatingContainerRef.current) {
      floatingContainerRef.current = createFloatingContainer();
    }
    const container = captchaRef.current ?? floatingContainerRef.current!;
    const widgetId = window.turnstile.render(container, {
      sitekey: TURNSTILE_SITE_KEY,
      action,
      execution: "execute",
      appearance: "interaction-only",
      theme: WIDGET_THEME,
      callback: (token: string) => settle(token),
      "error-callback": (code?: string) =>
        settle(null, new Error(`Turnstile error${code ? `: ${code}` : ""}`)),
      "expired-callback": () => settle(null, new Error("Turnstile token expired")),
      "timeout-callback": () => settle(null, new Error("Turnstile challenge timed out")),
    });
    if (!widgetId) {
      throw new Error("Unable to render the Turnstile widget");
    }
    widgetIdRef.current = widgetId;
    return widgetId;
  }, [action, settle]);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!isEnabled) return null;

    const widgetId = await ensureWidget();
    // Tokens are single-use: drop the previous one before asking for a new one.
    settle(null, new Error("Turnstile challenge superseded"));
    window.turnstile?.reset(widgetId);

    return new Promise<string>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        settle(null, new Error("Turnstile challenge timed out"));
      }, TOKEN_TIMEOUT_MS);

      pendingRef.current = {
        resolve: (token) => {
          window.clearTimeout(timeoutId);
          resolve(token);
        },
        reject: (error) => {
          window.clearTimeout(timeoutId);
          reject(error);
        },
      };

      window.turnstile?.execute(widgetId);
    });
  }, [ensureWidget, isEnabled, settle]);

  return { captchaRef, isEnabled, getToken };
};

export default useTurnstile;
